import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Activity, ShoppingBag, Globe } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    lang,
    toggleLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const navLinks = [{
    name: t('nav.team'),
    href: '#team'
  }, {
    name: t('nav.matches'),
    href: '#matches'
  }, {
    name: t('nav.deals'),
    href: '#deals'
  }];
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-argentina-black/95 backdrop-blur-md py-4 shadow-lg' : 'bg-transparent py-6'}`} data-id="element-276">
        <div className="container mx-auto px-4 md:px-12 flex justify-between items-center" data-id="element-277">
          {/* Logo Area */}
          <div className="flex items-center gap-2 md:gap-3 z-50 min-w-0" data-id="element-278">
            <a href="#" className="flex items-center shrink-0" data-id="element-279">
              <img src="https://cdn.magicpatterns.com/uploads/dHFF6t2sCE9DhuCAgyYxJp/ChatGPT_Image_Mar_30,_2026,_07_19_46_PM.png" alt="Qtap Logo" className="w-8 h-8 md:w-10 md:h-10 object-contain" decoding="async" data-id="element-280" />
            </a>
            <a href="https://shop.qtap.me/collections/world-cup-2026-smart-t-shirt" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-argentina-blue hover:bg-argentina-dark-blue text-white px-3 py-1.5 rounded-full font-oswald uppercase text-[10px] md:text-xs font-bold tracking-wider transition-colors duration-300 min-h-[32px] md:min-h-[36px]" data-id="element-281">
              <ShoppingBag size={13} data-id="element-282" />
              <span data-id="element-283">{t('nav.orderYours')}</span>
            </a>
            <button onClick={toggleLang} className="flex items-center gap-1 md:gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-2.5 md:px-3 py-1.5 rounded-full font-oswald uppercase text-[10px] md:text-xs font-bold tracking-wider transition-colors duration-300 min-h-[32px] md:min-h-[36px]" data-id="element-284">
              <Globe size={12} className="md:w-[13px] md:h-[13px]" data-id="element-285" />
              <span data-id="element-286">{lang === 'en' ? 'ES' : 'EN'}</span>
            </button>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-287">
            {navLinks.map(link => <a key={link.name} href={link.href} className="font-oswald uppercase tracking-widest text-sm font-medium hover:text-argentina-gold transition-colors duration-300 relative group min-h-[44px] flex items-center" data-id="element-288">
                {link.name}
                <span className="absolute bottom-1 left-0 w-0 h-0.5 bg-argentina-gold transition-all duration-300 group-hover:w-full" data-id="element-289"></span>
              </a>)}
            <a href="https://www.espn.com/soccer/scoreboard/_/league/fifa.world" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-argentina-gold text-argentina-black px-5 py-2 rounded-full font-oswald uppercase text-sm font-bold hover:bg-white transition-colors duration-300 min-h-[44px]" data-id="element-290">
              <Activity size={16} data-id="element-291" />
              <span data-id="element-292">{t('nav.liveScores')}</span>
            </a>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden z-[60] text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-293">
            {isMobileMenuOpen ? <X size={28} data-id="element-294" /> : <Menu size={28} data-id="element-295" />}
          </button>
        </div>
      </header>

      {/* Mobile Nav Dropdown — rendered OUTSIDE header to avoid stacking context issues */}
      <AnimatePresence data-id="element-296">
        {isMobileMenuOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/60 z-[52] md:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-297" />
            {/* Dropdown Panel */}
            <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="fixed top-0 left-0 right-0 z-[55] bg-argentina-black border-b border-white/10 shadow-2xl md:hidden" data-id="element-298">
              {/* Panel Header */}
              <div className="flex items-center justify-between px-4 py-4" data-id="element-299">
                <a href="#" className="flex items-center shrink-0" data-id="element-300">
                  <img src="https://cdn.magicpatterns.com/uploads/dHFF6t2sCE9DhuCAgyYxJp/ChatGPT_Image_Mar_30,_2026,_07_19_46_PM.png" alt="Qtap Logo" className="w-8 h-8 object-contain" decoding="async" data-id="element-301" />
                </a>
                <button className="text-white p-2 min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" data-id="element-302">
                  <X size={24} data-id="element-303" />
                </button>
              </div>

              {/* Nav Links */}
              <nav className="px-6 pb-6 flex flex-col gap-1" data-id="element-304">
                {navLinks.map((link, i) => <motion.a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.05
            }} className="font-oswald text-lg uppercase tracking-widest text-white hover:text-argentina-gold transition-colors py-3 border-b border-white/5" data-id="element-305">
                    {link.name}
                  </motion.a>)}
                <motion.a href="https://www.espn.com/soccer/scoreboard/_/league/fifa.world" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="flex items-center justify-center gap-2 bg-argentina-gold text-argentina-black w-full py-3.5 rounded-xl font-oswald uppercase text-sm font-bold mt-4 min-h-[44px]" data-id="element-306">
                  <Activity size={18} data-id="element-307" />
                  <span data-id="element-308">{t('nav.liveScores')}</span>
                </motion.a>
              </nav>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}